import { useState } from 'react';
import {
  BarChart2,
  BookOpen,
  Building2,
  Hotel,
  LayoutDashboard,
  LogOut,
  Menu,
  Percent,
  Settings,
  UsersRound,
  X,
} from 'lucide-react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useLogout } from '@/hooks/use-auth';
import { useAuthStore } from '@/store/auth.store';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const NAV_ITEMS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/bookings', icon: BookOpen, label: 'Bookings' },
  { to: '/clients', icon: UsersRound, label: 'Clients' },
  { to: '/hotels', icon: Hotel, label: 'Hotels' },
  { to: '/rates', icon: Percent, label: 'Rates' },
  { to: '/reports', icon: BarChart2, label: 'Reports' },
];

// Shared text-fade class: always visible on mobile, fades in on desktop hover
const FADE = 'opacity-100 transition-opacity duration-150 lg:opacity-0 lg:delay-75 lg:group-hover:opacity-100';

export function AppLayout() {
  const { user } = useAuthStore();
  const logout = useLogout();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="flex h-screen bg-neutral-50">
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/*
        Sidebar
        Mobile  : full-width (w-56) drawer, slides in/out via translate
        Desktop : icon rail (w-[52px]) → expands to w-56 on hover, smooth width transition
      */}
      <aside
        aria-label="Main navigation"
        className={cn(
          // Base
          'group fixed inset-y-0 left-0 z-30 flex flex-col border-r border-neutral-200 bg-white overflow-hidden',
          // Mobile geometry + slide toggle
          'w-56 transition-transform duration-200 ease-in-out',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop: icon rail → full on hover; override mobile slide
          'lg:static lg:shrink-0 lg:translate-x-0',
          'lg:w-[52px] lg:transition-[width] lg:duration-200 lg:ease-in-out lg:hover:w-56',
        )}
      >
        {/* ── Logo ───────────────────────────────────────────────────── */}
        <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-neutral-200 pl-[14px] pr-2">
          <Building2 className="h-6 w-6 shrink-0 text-tangerine-500" />
          <div className="flex-1 overflow-hidden">
            <p className={cn('whitespace-nowrap text-sm font-semibold text-neutral-900', FADE)}>
              Diverse Hotels
            </p>
            <p className={cn('whitespace-nowrap text-xs text-neutral-500', FADE)}>CRM</p>
          </div>
          {/* Mobile close button */}
          <button
            className="rounded p-1 text-neutral-500 hover:bg-neutral-100 lg:hidden"
            onClick={closeSidebar}
            aria-label="Close navigation"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Nav ────────────────────────────────────────────────────── */}
        <nav
          aria-label="App sections"
          className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3 space-y-0.5"
        >
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={closeSidebar}
              title={item.label}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-[10px] py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-tangerine-50 text-tangerine-600 font-medium'
                    : 'text-neutral-700 hover:bg-neutral-100',
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className={cn('whitespace-nowrap', FADE)}>{item.label}</span>
            </NavLink>
          ))}

          {user?.role === 'super_admin' && (
            <div className="pt-3">
              <p className={cn('px-[10px] pb-1 text-xs font-medium uppercase tracking-wide text-neutral-400 whitespace-nowrap', FADE)}>
                Settings
              </p>
              <NavLink
                to="/settings/users"
                onClick={closeSidebar}
                title="Users & Permissions"
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-md px-[10px] py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-tangerine-50 text-tangerine-600 font-medium'
                      : 'text-neutral-700 hover:bg-neutral-100',
                  )
                }
              >
                <Settings className="h-4 w-4 shrink-0" />
                <span className={cn('whitespace-nowrap', FADE)}>Users &amp; Permissions</span>
              </NavLink>
            </div>
          )}
        </nav>

        {/* ── User footer ─────────────────────────────────────────────── */}
        <div className="shrink-0 border-t border-neutral-200 p-3">
          <div className="flex items-center gap-2">
            {/* Avatar — always visible, serves as collapsed indicator */}
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-tangerine-100 text-xs font-semibold text-tangerine-700">
              {user?.fullName?.charAt(0).toUpperCase() ?? '?'}
            </div>
            <div className={cn('min-w-0 flex-1 overflow-hidden', FADE)}>
              <p className="truncate text-sm font-medium text-neutral-900">{user?.fullName}</p>
              <p className="truncate text-xs text-neutral-500">{user?.email}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => logout.mutate()}
              aria-label="Log out"
              className={cn('shrink-0', FADE)}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* ── Content wrapper ──────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Mobile top bar */}
        <div className="flex h-12 shrink-0 items-center gap-3 border-b border-neutral-200 bg-white px-4 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
            aria-expanded={sidebarOpen}
            className="rounded p-1 text-neutral-500 hover:bg-neutral-100"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Building2 className="h-5 w-5 text-tangerine-500" />
          <span className="text-sm font-semibold text-neutral-900">Diverse Hotels CRM</span>
        </div>

        {/* Main content */}
        <main id="main-content" className="flex-1 overflow-y-auto" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ── Shared page components ────────────────────────────────────────────────────

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between border-b border-neutral-200 bg-white px-6 py-4">
      <div>
        <h1 className="text-xl font-medium text-neutral-900">{title}</h1>
        {description && <p className="mt-0.5 text-sm text-neutral-500">{description}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="mb-4 inline-flex items-center gap-1 text-sm text-tangerine-600 hover:underline">
      ← {label}
    </Link>
  );
}
